import { Color, Spacing } from '@persona-id/design-tokens';
import { LinkIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { LoaderIcon } from '@persona-id/design-tokens/icons/24px';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { NodeId } from 'dashboard/models/graph';
import Button from 'design-system/components/Button';
import Modal from 'design-system/components/Modal';
import { rotate } from 'design-system/variables/keyframes';
import EventsClient, { GraphEvent } from 'lib/clients/EventsClient';

const Title = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  padding: ${Spacing[1]};
  user-select: none;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  margin-left: auto;
`;

const SnapshotLinkContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${Color.CoreSlate100};
  border-radius: 4px;
  overflow: hidden;
  margin-top: ${Spacing[3]};
  width: 443px;
`;

const SnapshotLink = styled.input`
  flex-grow: 1;
  padding: ${Spacing[2]};
  border: none;
  background: transparent;
  font-size: 14px;
  color: ${Color.CorePurple800};
  &:focus {
    outline: none;
  }
`;

const CopyButton = styled(Button)`
  margin: ${Spacing[1]};
  width: 70px;
`;

const Spinner = styled(LoaderIcon)`
  width: 400px;
  height: ${Spacing[3]};
  color: ${({ theme }) => theme.color.primary.brand700};
  animation: ${rotate} 1s linear infinite;
`;

export interface GraphExplorerHeaderProps {
  handleExportClick: (nodeIds: NodeId[]) => void;
  handleSnapshotClick: () => Promise<string | void> | undefined;
  loadingCreateGraphSnapshot: boolean;
}

const GraphExplorerHeader = ({
  handleExportClick,
  handleSnapshotClick,
  loadingCreateGraphSnapshot,
}: GraphExplorerHeaderProps) => {
  const { exportNodes } = useGraphExplorer();
  const [snapshotLink, setSnapshotLink] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleExportButtonClick = useCallback(() => {
    handleExportClick(exportNodes);
    EventsClient.logAll(GraphEvent.GraphExport);
  }, [exportNodes, handleExportClick]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(snapshotLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  };

  const handleOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setSnapshotLink('');
      setIsCopied(false);
    }
  };

  const handleSnapshotButtonClick = useCallback(async () => {
    const snapshotURL = await handleSnapshotClick();
    if (snapshotURL) {
      setIsModalOpen(true);
      setSnapshotLink(snapshotURL);
    }
  }, [handleSnapshotClick]);

  return (
    <FullScreenHeaderContainer>
      <Title>Graph</Title>
      <ButtonContainer>
        <FeatureFlagged featureFlag='dashboard:graph-snapshots-feature'>
          <Button
            id='graph-explorer-header__snapshot-button'
            variant='basic'
            width='auto'
            prefixIcon={<LinkIcon />}
            disabled={exportNodes.length === 0 || loadingCreateGraphSnapshot}
            onClick={handleSnapshotButtonClick}
          >
            {loadingCreateGraphSnapshot ? <Spinner /> : 'Snapshot'}
          </Button>
          <Modal
            title='Share snapshot'
            size='auto'
            open={isModalOpen}
            onOpenChange={handleOpenChange}
          >
            <SnapshotLinkContainer>
              <SnapshotLink value={snapshotLink} readOnly />
              <CopyButton
                id='graph-explorer-header__copy-link'
                variant='basic'
                onClick={handleCopyLink}
                disabled={isCopied}
              >
                {isCopied ? 'Copied' : 'Copy'}
              </CopyButton>
            </SnapshotLinkContainer>
          </Modal>
        </FeatureFlagged>
        <Button
          id='graph-explorer__export'
          variant='basic'
          onClick={handleExportButtonClick}
          data-test='graph-explorer__export'
          width='auto'
          disabled={exportNodes.length === 0}
          prefixIcon={<TransferOutIcon />}
        >
          Export Accounts
        </Button>
      </ButtonContainer>
    </FullScreenHeaderContainer>
  );
};

export default GraphExplorerHeader;
